/**
 * BulkBar — selection chrome and bulk actions for both inboxes (#627).
 *
 * The part that earns its keep is the ESCALATION BANNER. Both inboxes scroll
 * infinitely, so a header tick selects the rows that happen to be loaded. When
 * more exist behind them, saying so — and offering the explicit upgrade — is
 * the difference between "archive these 40" and "archive all 248".
 *
 *     [✓] header checkbox  →  selects every LOADED row, and only those
 *
 * There is deliberately no "Select all N in <folder> →" escalation any more
 * (#710). It switched the selection to a server-side predicate, and the
 * destructive verbs cannot expand one — so the bar went on offering Delete
 * while the handlers refused it. Selection is now exactly what is ticked, and
 * scrolling more rows into view does not add them.
 */
import React from 'react';
import {
    Archive,
    ArchiveRestore,
    ChevronsRight,
    Clock,
    MailCheck,
    MailOpen,
    Tag,
    Trash2,
    X,
} from 'lucide-react';

import { Checkbox } from '../ui/checkbox';

/**
 * Every key `bulkActionsFor` can return, with the icon and title it renders.
 *
 * One definition per RESOLVER key rather than one filtered list: a key the
 * resolver returns but this map cannot describe would be an invisible control
 * — the click target simply would not exist. `definitionsFor` throws on such a
 * key instead, so the gap surfaces as a loud test failure at the contract's
 * two ends rather than as a silently missing button.
 *
 * `markRead`/`markUnread` and `markImportant`/`unmarkImportant` share icons
 * with their row-strip counterparts (`mailRowState.actionDef`), so the bulk
 * verb and the per-row control stay recognisably the same thing (#695).
 */
export const ACTION_DEFS = Object.freeze({
    archive: { icon: Archive, label: 'Archive' },
    unarchive: { icon: ArchiveRestore, label: 'Move to Inbox' },
    delete: { icon: Trash2, label: 'Delete' },
    markRead: { icon: MailCheck, label: 'Mark read' },
    markUnread: { icon: MailOpen, label: 'Mark unread' },
    snooze: { icon: Clock, label: 'Snooze' },
    unsnooze: { icon: Clock, label: 'Unsnooze' },
    markImportant: { icon: ChevronsRight, label: 'Mark important' },
    unmarkImportant: { icon: ChevronsRight, label: 'Remove important' },
    restore: { icon: ArchiveRestore, label: 'Restore' },
    // #646 — opens the surface's tag picker over the selected conversations.
    tag: { icon: Tag, label: 'Tag' },
});

/** Resolve keys to renderable definitions, in the order the resolver gave. */
const definitionsFor = (keys) => keys.map((key) => {
    const definition = ACTION_DEFS[key];
    if (!definition) throw new Error(`Missing bulk action definition: ${key}`);
    return { key, ...definition };
});

export default function BulkBar({
    selection,
    folderLabel = 'Inbox',
    /**
     * One entry per GRID COLUMN of the rows below, so the labels sit directly
     * over the cells they name. Index 0 is the checkbox column and is ignored.
     * Entries may be nodes, so a label can also be a sort button.
     */
    columnCells = null,
    /** The row grid this header aligns to — `surface.gridTemplate`. */
    gridTemplate,
    onAction,
    actions = [],
}) {
    const {
        selectedCount,
        allLoadedSelected,
        someSelected,
        toggleAllLoaded,
        clear,
    } = selection;

    const active = selectedCount > 0;

    return (
        <div
            data-testid="mail-bulk-bar"
            className="bg-[#FBFAF9] border-b border-[#EDEBE6]"
        >
            {/* The header shares the ROWS' grid, not a flex layout of its
                own: the labels have to sit over the cells they name, and two
                independent width systems cannot be kept in step. `px-5` and
                `gap-[14px]` match MailRow exactly for the same reason. */}
            <div
                className="grid items-center gap-[14px] px-5 py-2"
                style={{ gridTemplateColumns: gridTemplate }}
            >
                <Checkbox
                    checked={
                        allLoadedSelected ? true : someSelected ? 'indeterminate' : false
                    }
                    aria-label="Select all loaded messages"
                    data-testid="mail-select-all"
                    onCheckedChange={(checked) => toggleAllLoaded(!!checked)}
                />

                {active ? (
                    <div
                        className="flex items-center gap-3 min-w-0"
                        style={{ gridColumn: '2 / -1' }}
                    >
                        <div className="flex items-center gap-[2px]">
                            {definitionsFor(actions).map(({ key, icon: Icon, label }) => (
                                <button
                                    key={key}
                                    type="button"
                                    title={label}
                                    aria-label={label}
                                    data-testid={`mail-bulk-${key}`}
                                    onClick={() => onAction(key, null, { source: 'bulk' })}
                                    className="w-[26px] h-[26px] rounded-[7px] grid place-items-center text-stone-600 hover:bg-stone-200"
                                >
                                    <Icon className="w-[15px] h-[15px]" />
                                </button>
                            ))}
                        </div>
                        <span
                            className="text-[12px] text-stone-600"
                            data-testid="mail-bulk-count"
                        >
                            {`${selectedCount} selected`}
                        </span>
                        <button
                            type="button"
                            onClick={clear}
                            aria-label="Clear selection"
                            data-testid="mail-bulk-cancel"
                            className="w-[26px] h-[26px] rounded-[7px] grid place-items-center text-stone-500 hover:bg-stone-200"
                        >
                            <X className="w-[15px] h-[15px]" />
                        </button>
                    </div>
                ) : (
                    /* §11.2: 11px, 600, #8A837E. A surface with no labels
                       (`showColumnHeader: false`) renders empty cells, which is
                       what keeps the personal row a bare select-all. */
                    (columnCells || []).slice(1).map((cell, index) => (
                        <span
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                            data-testid={cell ? 'mail-bulk-column-label' : undefined}
                            className="truncate text-[11px] font-semibold tracking-wider text-[#8A837E] uppercase"
                        >
                            {cell}
                        </span>
                    ))
                )}
            </div>

        </div>
    );
}
